/**
 * 템플릿 렌더러 — 검증된 데이터를 HTML 문자열로.
 *
 * **순수 함수다.** DOM 도, `Date.now()` 도, 네트워크도 만지지 않는다. 시각은 인자로
 * 주입된다. 그래서 서버(썸네일·검증), 플레이어(화면), 콘솔(미리보기)이 같은 코드를
 * 쓰고, 테스트가 임의 시각의 출력을 그대로 검사할 수 있다.
 *
 * **모든 사용자 입력은 이스케이프한다.** 운영자가 콘솔에 입력한 문자열이 그대로
 * 플레이어 DOM 에 들어간다 — 여기서 새면 공공 화면에 임의 스크립트가 실행된다.
 * 값을 넣는 통로는 `esc()` 하나뿐이고, 이스케이프 없이 문자열을 이어붙이는 곳은
 * 이 파일 안에 없어야 한다.
 */
import { zonedFieldsAt } from '../time.js';
import { DEFAULT_TEMPLATE_FONT } from './fonts.js';
import { icon } from './icons.js';
import { htmlAreaSchema, templatePayloadSchema, ZERO_ACCIDENT_MULTIPLIERS, ZONE_COUNTS, } from './schema.js';
import { CANVAS_HEIGHT, CANVAS_WIDTH, PORTRAIT_CANVAS_HEIGHT, PORTRAIT_CANVAS_WIDTH } from './styles.js';
/** HTML 이스케이프. 값이 마크업으로 해석되는 경로를 여기 하나로 모은다. */
export function esc(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
/** 숫자를 CSS 에 넣을 때. 스타일 주입을 막기 위해 숫자로 강제한다. */
function num(value, min = 0, max = 100) {
    if (!Number.isFinite(value))
        return min;
    return Math.min(max, Math.max(min, value));
}
const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];
/** 그 시간대의 오늘(연·월·일). 모르는 시간대면 UTC 로 — 화면이 멈추는 것보다 하루 어긋나는 편이 낫다. */
export function zonedDay(now, timezone) {
    const format = (timeZone) => new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(now);
    let parts;
    try {
        parts = format(timezone);
    }
    catch {
        parts = format('UTC');
    }
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? '');
    return { year: get('year'), month: get('month'), day: get('day') };
}
function formatClock(context) {
    const fields = zonedFieldsAt(context.now, context.timezone);
    const hours = String(Math.floor(fields.minutes / 60)).padStart(2, '0');
    const minutes = String(fields.minutes % 60).padStart(2, '0');
    // 연·월·일은 분 단위 필드에 없으므로 타임존 기준으로 따로 뽑는다.
    const { year, month, day: dayOfMonth } = zonedDay(context.now, context.timezone);
    return {
        date: `${year}년 ${month}월 ${dayOfMonth}일 ${DAY_NAMES[fields.day]}요일`,
        time: `${hours}:${minutes}`,
        day: fields.day,
        month,
        dayOfMonth,
    };
}
/**
 * 템플릿 HTML 에서 시각 따라 그 자리에서 고치는 부분을 비운 글 — 둘이 같으면 다시 그릴 까닭이 없다(2026-11-03 플레이어에서,
 * 2026-11-04 콘솔 미리보기도 쓰려고 여기로). 머리글 시계·카운트다운 숫자, 구역 나누기의 지금 장 표시를 비운다. 다시 그리면
 * 떠 있는 동안의 움직임(`TEMPLATE_LIVE_CLASS`)이 처음부터 다시 돈다.
 */
export function templateLiveKey(html) {
    return html
        .replace(/(<span class="so-tpl__(?:date|time)">)[^<]*(<\/span>)/g, '$1$2')
        .replace(/(<div class="so-promo__countdown-value">)[^<]*(<\/div>)/g, '$1$2')
        .replace(/ so-zone__slide--on/g, '');
}
/**
 * 머리글 시계의 날짜·시각 글 — 화면에 떠 있는 템플릿의 시계를 다시 그리지 않고 그 자리에서 고쳐 쓸 때(2026-11-03, 플레이어
 * `template-live.ts`). 렌더러가 그리는 글과 같다.
 */
export function templateClockText(now, timezone) {
    const clock = formatClock({ location: '', now, timezone });
    return { date: clock.date, time: clock.time };
}
/**
 * 글 길이에 맞춘 글자 크기 등급 — 1 이 가장 크다(`so-t1` ~ `so-t4`).
 *
 * 사이니지의 글은 멀리서 읽혀야 하니 **짧으면 크게** 쓴다. 대신 길면 같은 크기로는 칸을
 * 넘친다. 화면에서 재 볼 수 없는 순수 함수라(서버·콘솔·플레이어가 같은 그림을 내야 한다)
 * 글자 수로 등급을 정한다. `limits` 는 등급이 한 단씩 내려가는 글자 수다.
 */
export function sizeTier(text, limits) {
    const length = Array.from(text.trim()).length;
    if (length <= limits[0])
        return 1;
    if (length <= limits[1])
        return 2;
    if (length <= limits[2])
        return 3;
    return 4;
}
const tierClass = (text, limits) => `so-t${sizeTier(text, limits)}`;
/** 나란히 놓인 같은 종류의 글(조치 목록·통계)은 한 크기로 — 가장 긴 것이 정한다. 짧은 칸만 크면 더 중요해 보인다. */
const groupTier = (texts, limits) => tierClass(texts.reduce((a, b) => (Array.from(b).length > Array.from(a).length ? b : a), ''), limits);
function header(context, variant, live) {
    const clock = formatClock(context);
    const brand = (context.brand ?? '').trim();
    const logo = context.logoUrl
        ? `<img class="so-tpl__brand-logo" src="${esc(context.logoUrl)}" alt="">`
        : '';
    // 장소는 환경 설정이 정한 글(`headerPlace`) — 적지 않기로 했거나 비었으면 그리지 않는다(2026-11-03).
    const place = (context.location ?? '').trim();
    return (`<header class="so-tpl__header so-tpl__header--${variant}">` +
        `<div class="so-tpl__brand">` +
        logo +
        (brand ? `<span class="so-tpl__brand-name">${esc(brand)}</span>` : '') +
        // 구분선은 이름과 장소가 둘 다 있을 때만 — 홀로 선 `|` 는 무엇이 빠졌는지만 드러낸다.
        (brand && place ? `<span class="so-tpl__brand-sep">|</span>` : '') +
        (place ? `<span class="so-tpl__brand-place">${esc(place)}</span>` : '') +
        `</div>` +
        `<div class="so-tpl__clock">` +
        (live ? `<span class="so-tpl__live"><span class="so-tpl__live-dot"></span>LIVE</span>` : '') +
        `<span class="so-tpl__date">${esc(clock.date)}</span>` +
        `<span class="so-tpl__time">${esc(clock.time)}</span>` +
        `</div>` +
        `</header>`);
}
/**
 * 티커가 한 벌을 흘려보내는 시간(초). 글이 길수록 오래 — 한결같은 속도로 읽힌다.
 *
 * 34px 글자 하나가 대략 한 글자 폭이고, 초당 네 글자쯤이 지나가며 읽을 수 있는 빠르기다.
 * 짧은 글이 휙 지나가지 않게 아래를 막는다.
 */
export function tickerSeconds(content) {
    return Math.max(16, Math.round(Array.from(content).length / 4));
}
/**
 * 티커.
 *
 * 내용을 **두 번** 쓴다. 애니메이션이 `-50%` 로 이동하므로 한 벌만 두면 끝에서
 * 툭 끊겨 되돌아간다(프로토타입이 그랬다). 두 벌이면 이음매 없이 흐른다.
 */
function footer(variant, ticker, temperature) {
    const content = esc(ticker.content);
    const separator = '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;';
    // 숫자로만 만든 값이라 style 에 넣어도 주입이 생기지 않는다.
    const seconds = tickerSeconds(ticker.content);
    return (`<footer class="so-tpl__footer so-tpl__footer--${variant}">` +
        `<div class="so-tpl__ticker">` +
        `<span class="so-tpl__ticker-label">${esc(ticker.label)}</span>` +
        `<div class="so-tpl__ticker-track">` +
        `<div class="so-tpl__ticker-move" style="-webkit-animation-duration:${seconds}s;animation-duration:${seconds}s">` +
        `${content}${separator}${content}${separator}</div>` +
        `</div></div>` +
        (temperature ? `<span class="so-tpl__temp">${esc(temperature)}</span>` : '') +
        `</footer>`);
}
function shell(context, ticker, body, options = {}) {
    const variant = options.variant ?? 'default';
    return (`<div class="so-tpl ${options.className ?? 'so-tpl--dark'}">` +
        header(context, variant, options.live ?? false) +
        `<main class="so-tpl__main">${body}</main>` +
        footer(variant, ticker, options.temperature ?? null) +
        `</div>`);
}
// ─────────────────────────────── 템플릿별 ───────────────────────────────
function renderAnnouncement(data, context) {
    const notices = data.notices
        .map((notice) => `<div class="so-ann__notice">` +
        `<span class="so-ann__notice-title so-one">${esc(notice.title)}</span>` +
        `<span class="so-ann__notice-dept so-one">${esc(notice.dept)}</span>` +
        `</div>`)
        .join('');
    const weather = data.weather
        ? `<div class="so-ann__weather">` +
            `<div class="so-ann__weather-main">${icon('sun')}` +
            `<div class="so-ann__weather-text"><div class="so-ann__weather-temp so-one">${esc(data.weather.temperature)}</div>` +
            `<div class="so-ann__weather-cond so-one">${esc(data.weather.condition)}</div></div></div>` +
            `<div class="so-ann__weather-place so-one">${esc(data.weather.place)}</div>` +
            `</div>`
        : '';
    const hasSide = data.notices.length > 0 || data.weather !== null;
    const body = `<div class="so-body so-ann">` +
        `<section class="so-card so-col so-ann__main">` +
        `<div><span class="so-badge">${icon('bell')}<span class="so-one">${esc(data.badge)}</span></span></div>` +
        `<div class="so-ann__body">` +
        `<h1 class="so-ann__title so-clamp4 ${tierClass(data.title, [14, 28, 48])}">${esc(data.title)}</h1>` +
        (data.body
            ? `<p class="so-ann__lead so-clamp4 ${tierClass(data.body, [60, 120, 220])}">${esc(data.body)}</p>`
            : '') +
        `</div>` +
        `<div class="so-ann__meta">` +
        `<span class="so-ann__meta-item">${icon('clock')}<span class="so-one">${esc(data.date)}</span></span>` +
        `<span class="so-ann__meta-item">${icon('user')}<span class="so-one">${esc(data.author)}</span></span>` +
        `</div>` +
        `</section>` +
        (hasSide
            ? `<aside class="so-col so-ann__side">` +
                (data.notices.length > 0
                    ? `<div class="so-card so-ann__notices"><h3>최근 공지</h3>` +
                        `<div class="so-ann__notice-list">${notices}</div></div>`
                    : '') +
                weather +
                `</aside>`
            : '') +
        `</div>`;
    return shell(context, data.ticker, body, {
        temperature: data.weather?.temperature ?? null,
    });
}
/** 장식용 QR 패턴. 실제 인코딩이 아니다 — 스키마 주석 참고. */
function decorativeQr() {
    const cells = [
        [0, 0, 7, 7],
        [26, 0, 7, 7],
        [0, 26, 7, 7],
    ];
    const outer = cells
        .map(([x, y, w, h]) => `<rect fill="currentColor" x="${x}" y="${y}" width="${w}" height="${h}"/>` +
        `<rect fill="#fff" x="${x + 1}" y="${y + 1}" width="5" height="5"/>` +
        `<rect fill="currentColor" x="${x + 2}" y="${y + 2}" width="3" height="3"/>`)
        .join('');
    const speckle = [
        [8, 0, 1, 1], [10, 0, 2, 1], [15, 0, 3, 1], [20, 0, 2, 1],
        [8, 1, 2, 1], [14, 1, 2, 1], [21, 1, 3, 1],
        [9, 3, 1, 1], [12, 3, 3, 1], [19, 3, 2, 1],
        [10, 10, 2, 2], [16, 12, 3, 2], [22, 9, 2, 3], [12, 20, 4, 2], [20, 22, 2, 4],
    ]
        .map(([x, y, w, h]) => `<rect fill="currentColor" x="${x}" y="${y}" width="${w}" height="${h}"/>`)
        .join('');
    return `<svg viewBox="0 0 33 33" aria-hidden="true">${outer}${speckle}</svg>`;
}
function renderPromotion(data, context) {
    const benefits = data.benefits
        .map((benefit) => `<div class="so-promo__benefit">` +
        `<div class="so-tile-icon so-promo__benefit-icon">${icon(benefit.icon)}</div>` +
        `<div class="so-promo__benefit-text"><div class="so-promo__benefit-title so-one">${esc(benefit.title)}</div>` +
        `<div class="so-promo__benefit-desc so-one">${esc(benefit.description)}</div></div>` +
        `</div>`)
        .join('');
    const countdown = data.deadline ? renderCountdown(data.deadline, data.deadlineLabel, context.now) : '';
    const qr = data.qr
        ? `<div class="so-promo__qr">` +
            `<div class="so-promo__qr-box">${decorativeQr()}</div>` +
            `<div class="so-promo__qr-text"><div class="so-promo__qr-caption so-one">${esc(data.qr.caption)}</div>` +
            `<div class="so-promo__qr-hint so-one">${esc(data.qr.hint)}</div></div>` +
            `</div>`
        : '';
    // 제목과 강조는 한 덩어리로 읽힌다 — 둘을 합친 길이가 크기를 정한다. 각각 두 줄까지.
    const headline = `${data.title}${data.titleAccent}`;
    const body = `<div class="so-body so-promo">` +
        `<section class="so-col so-promo__visual">` +
        `<div><span class="so-badge so-badge--glass">${icon('star')}<span class="so-one">${esc(data.badge)}</span></span></div>` +
        `<div class="so-promo__headline">` +
        `<h1 class="so-promo__title ${tierClass(headline, [14, 26, 44])}"><span class="so-clamp2">${esc(data.title)}</span>` +
        (data.titleAccent ? `<em class="so-clamp2">${esc(data.titleAccent)}</em>` : '') +
        `</h1>` +
        (data.body
            ? `<p class="so-promo__lead so-clamp3 ${tierClass(data.body, [50, 100, 180])}">${esc(data.body)}</p>`
            : '') +
        `</div>${qr}</section>` +
        `<aside class="so-col so-promo__side">` +
        `<div class="so-card so-promo__amount">` +
        `<div class="so-promo__amount-label so-one">${esc(data.amountLabel)}</div>` +
        `<div class="so-promo__amount-value so-one ${tierClass(data.amount, [8, 12, 18])}">${esc(data.amount)}</div></div>` +
        `<div class="so-promo__benefits">${benefits}</div>` +
        countdown +
        `</aside></div>`;
    return shell(context, data.ticker, body, { variant: 'dark', className: 'so-tpl--dark' });
}
/** 남은 시간 계산. 지난 마감은 전부 0 으로 — 음수가 화면에 뜨는 것보다 낫다. */
export function remainingParts(deadline, now) {
    const totalSeconds = Math.max(0, Math.floor((deadline.getTime() - now.getTime()) / 1000));
    return {
        days: Math.floor(totalSeconds / 86_400),
        hours: Math.floor((totalSeconds % 86_400) / 3_600),
        minutes: Math.floor((totalSeconds % 3_600) / 60),
        seconds: totalSeconds % 60,
    };
}
function renderCountdown(deadline, label, now) {
    const remaining = remainingParts(deadline, now);
    const units = [
        [remaining.days, '일'],
        [remaining.hours, '시'],
        [remaining.minutes, '분'],
        [remaining.seconds, '초'],
    ];
    const cells = units
        .map(([value, unit]) => `<div><div class="so-promo__countdown-value">${String(value).padStart(2, '0')}</div>` +
        `<div class="so-promo__countdown-unit">${esc(unit)}</div></div>`)
        .join('');
    return (`<div class="so-promo__countdown">` +
        `<div class="so-promo__countdown-label so-one">${esc(label)}</div>` +
        `<div class="so-promo__countdown-row">${cells}</div></div>`);
}
function renderAlert(data, context) {
    const statTier = groupTier(data.stats.map((stat) => stat.value), [9, 14, 20]);
    const stats = data.stats
        .map((stat) => `<div class="so-alert__stat">` +
        `<div class="so-alert__stat-label so-one">${esc(stat.label)}</div>` +
        `<div class="so-alert__stat-value so-one ${statTier}">${esc(stat.value)}</div></div>`)
        .join('');
    const actionTier = groupTier(data.actions, [22, 34, 46]);
    const actions = data.actions
        .map((action, index) => `<div class="so-alert__action">` +
        `<div class="so-tile-icon so-alert__action-num">${index + 1}</div>` +
        `<span class="so-alert__action-text so-clamp2 ${actionTier}">${esc(action)}</span></div>`)
        .join('');
    const body = `<div class="so-body so-col so-alert">` +
        `<section class="so-alert__head">` +
        `<div class="so-alert__icon">` +
        `<div class="so-alert__icon-core">${icon('alert')}</div>` +
        `<div class="so-alert__icon-label so-one">${esc(data.iconLabel)}</div></div>` +
        `<div class="so-alert__heading">` +
        `<div class="so-alert__eyebrow">` +
        `<span class="so-alert__eyebrow-dot"></span>` +
        `<span class="so-alert__eyebrow-text">${esc(data.label)}</span></div>` +
        `<h1 class="so-alert__title so-clamp2 ${tierClass(data.title, [14, 26, 44])}">${esc(data.title)}</h1></div>` +
        `</section>` +
        `<div class="so-alert__stats">${stats}</div>` +
        `<div class="so-alert__actions">${actions}</div>` +
        `</div>`;
    return shell(context, data.ticker, body, { variant: 'alert', className: 'so-tpl--alert' });
}
/**
 * 일정/이벤트 — 세 단. 왼쪽 오늘(날짜·D-데이), 가운데 오늘의 일정, 오른쪽 다가오는 행사·통계.
 *
 * **세 단 모두 위아래 끝이 같은 카드다.** 가운데 목록만 카드 없이 떠 있던 때는 일정이 적으면
 * 아래가 통째로 비고, 양옆 카드와 끝선이 어긋나 보였다. 지금은 일정 줄이 카드 높이를 나눠
 * 갖는다 — 적으면(3개 이하) 글자를 키워 한 줄이 크게, 많으면 촘촘히.
 *
 * 장식 그라데이션은 오늘 카드 하나다. D-데이는 그 카드 아래 띠에, 다가오는 행사는 어두운
 * 카드에 보라 글자로 — 색 덩어리가 셋이던 때는 서로 다퉈 무엇부터 볼지 몰랐다.
 */
function renderSchedule(data, context) {
    const clock = formatClock(context);
    const density = data.events.length <= 3 ? 'few' : 'many';
    const events = data.events
        .map((event) => `<div class="so-sched__event">` +
        `<div class="so-sched__event-time">${esc(event.time)}</div>` +
        `<div class="so-sched__event-body">` +
        `<div class="so-sched__event-title so-one ${tierClass(event.title, [12, 18, 26])}">${esc(event.title)}</div>` +
        `<div class="so-sched__event-place">${icon('mapPin')}<span>${esc(event.location)}</span></div>` +
        `</div></div>`)
        .join('');
    const countdown = data.countdown
        ? `<div class="so-sched__countdown">` +
            `<div class="so-sched__countdown-label so-one">${esc(data.countdown.label)}</div>` +
            `<div class="so-sched__countdown-title so-clamp2">${esc(data.countdown.title)}</div></div>`
        : '';
    const today = `<aside class="so-col so-sched__today">` +
        `<div class="so-sched__date">` +
        `<div class="so-sched__date-month">${clock.month}월</div>` +
        `<div class="so-sched__date-day">${clock.dayOfMonth}</div>` +
        `<div class="so-sched__date-weekday">${esc(DAY_NAMES[clock.day] ?? '')}요일</div>` +
        `</div>${countdown}</aside>`;
    const list = `<section class="so-card so-col so-sched__list">` +
        `<div class="so-sched__list-head">` +
        `<h2 class="so-sched__heading so-one">${esc(data.heading)}</h2>` +
        `<span class="so-sched__count">${data.events.length}건</span></div>` +
        `<div class="so-sched__events">${events}</div></section>`;
    const upcoming = data.upcoming
        ? `<div class="so-card so-sched__upcoming">` +
            `<div class="so-sched__upcoming-head">` +
            `<div class="so-sched__upcoming-label so-one">${esc(data.upcoming.label)}</div>` +
            `<h3 class="so-sched__upcoming-title so-clamp2">${esc(data.upcoming.title)}</h3></div>` +
            `<div class="so-sched__upcoming-body">` +
            data.upcoming.items
                .map((item) => `<div class="so-sched__upcoming-item">${icon(item.icon)}<span>${esc(item.text)}</span></div>`)
                .join('') +
            `</div></div>`
        : '';
    // 행사가 없으면 통계가 오른쪽 단을 채운다 — 위에 붙어 아래가 비지 않게.
    const statTier = data.stats ? groupTier(data.stats.items.map((stat) => stat.value), [4, 6, 8]) : '';
    const stats = data.stats
        ? `<div class="so-card so-sched__stats${upcoming ? '' : ' so-sched__stats--fill'}">` +
            `<h4 class="so-one">${esc(data.stats.heading)}</h4>` +
            `<div class="so-sched__stats-grid">` +
            data.stats.items
                .map((stat) => `<div class="so-sched__stat">` +
                `<div class="so-sched__stat-value so-one ${statTier}">${esc(stat.value)}</div>` +
                `<div class="so-sched__stat-label so-one">${esc(stat.label)}</div></div>`)
                .join('') +
            `</div></div>`
        : '';
    const right = upcoming || stats ? `<aside class="so-col so-sched__right">${upcoming}${stats}</aside>` : '';
    const body = `<div class="so-body so-sched so-sched--${density}">${today}${list}${right}</div>`;
    return shell(context, data.ticker, body, {});
}
/**
 * 대시보드 — 위에 KPI, 아래에 차트·순위·진행률.
 *
 * **위아래 칸의 경계선이 한 줄에 선다.** 옆 패널이 있으면 차트는 KPI 두 칸 몫(사이 여백 포함),
 * 순위·진행률은 한 칸씩이다(`styles.ts` 의 `--half`). 비율로 나누던 때는 세로 경계가 어긋났다.
 *
 * KPI 가 둘 이하면 카드가 넓어진다 — 그때는 아이콘·이름을 왼쪽에, 값을 오른쪽에 두는 가로판이다.
 * 세로판 그대로 넓히면 왼쪽 끝 값과 오른쪽 끝 아이콘 사이가 텅 빈다.
 */
function renderDashboard(data, context) {
    const wide = data.kpis.length <= 2;
    const kpis = data.kpis
        .map((kpi) => {
        const tile = `<span class="so-tile-icon so-dash__kpi-icon so-tone-${kpi.tone}">${icon(kpi.icon)}</span>`;
        const label = `<span class="so-dash__kpi-label">${esc(kpi.label)}</span>`;
        const value = `<div class="so-dash__kpi-value ${tierClass(kpi.value, [6, 9, 13])}">${esc(kpi.value)}</div>`;
        const change = `<div class="so-dash__kpi-change so-dash__kpi-change--${kpi.up ? 'up' : 'down'}">` +
            `${icon(kpi.up ? 'trendUp' : 'trendDown')}<span class="so-one">${esc(kpi.change)}</span></div>`;
        return wide
            ? `<div class="so-card so-dash__kpi so-dash__kpi--wide">${tile}` +
                `<div class="so-dash__kpi-text">${label}${change}</div>${value}</div>`
            : `<div class="so-card so-dash__kpi"><div class="so-dash__kpi-head">${tile}${label}</div>${value}${change}</div>`;
    })
        .join('');
    const legend = data.chart.series
        .map((series) => `<span class="so-dash__legend-item">` +
        `<span class="so-dash__legend-dot so-dot-${series.tone}"></span><span class="so-one">${esc(series.name)}</span></span>`)
        .join('');
    // 막대는 칸 가운데 상자 안에 절대 위치로 세운다 — 계열이 하나면 상자를 채우고, 둘이면 나란히(`styles.ts`).
    const solo = data.chart.series.length === 1;
    const slots = solo ? ['solo'] : ['first', 'second'];
    const bars = data.chart.categories
        .map((category, index) => {
        const columns = data.chart.series
            .map((series, at) => `<div class="so-dash__bar so-dash__bar--${slots[at]} so-bar-${series.tone}" style="height:${num(series.values[index] ?? 0)}%"></div>`)
            .join('');
        return (`<div class="so-dash__bar-group">` +
            `<div class="so-dash__bar-slot"><div class="so-dash__bar-box${solo ? ' so-dash__bar-box--solo' : ''}">${columns}</div></div>` +
            `<span class="so-dash__bar-label">${esc(category)}</span></div>`);
    })
        .join('');
    const leaderboard = data.leaderboard
        ? `<div class="so-card so-dash__panel">` +
            `<div class="so-dash__panel-head">` +
            `<h3 class="so-dash__panel-title">${esc(data.leaderboard.title)}</h3>` +
            (data.leaderboard.live
                ? `<span class="so-dash__live"><span class="so-dash__live-dot"></span>실시간</span>`
                : '') +
            `</div><div class="so-dash__rows">` +
            data.leaderboard.rows
                .map((row, index) => {
                const rank = index + 1;
                const rankClass = rank <= 3 ? `so-rank-${rank}` : 'so-rank-n';
                return (`<div class="so-dash__row">` +
                    `<div class="so-dash__rank ${rankClass}">${rank}</div>` +
                    `<div class="so-dash__row-body">` +
                    `<div class="so-dash__row-name">${esc(row.name)}</div>` +
                    `<div class="so-dash__row-dept">${esc(row.dept)}</div></div>` +
                    `<div class="so-dash__row-score">${esc(row.score)}</div></div>`);
            })
                .join('') +
            `</div></div>`
        : '';
    const tones = ['blue', 'green', 'purple', 'orange'];
    const progress = data.progress
        ? `<div class="so-card so-dash__panel">` +
            `<div class="so-dash__panel-head"><h3 class="so-dash__panel-title">${esc(data.progress.title)}</h3></div>` +
            `<div class="so-dash__rows">` +
            data.progress.rows
                .map((row, index) => {
                const tone = tones[index % tones.length];
                return (`<div class="so-dash__row so-dash__progress-item">` +
                    `<div class="so-dash__progress-head">` +
                    `<span class="so-dash__progress-name">${esc(row.name)}</span>` +
                    `<span class="so-dash__progress-pct">${num(row.percent)}%</span></div>` +
                    `<div class="so-dash__progress-track">` +
                    `<div class="so-dash__progress-fill so-fill-${tone}" style="width:${num(row.percent)}%"></div>` +
                    `</div></div>`);
            })
                .join('') +
            `</div></div>`
        : '';
    const body = `<div class="so-body so-dash">` +
        `<div class="so-dash__kpis">${kpis}</div>` +
        `<div class="so-dash__charts">` +
        `<div class="so-card so-dash__panel so-dash__panel--wide${leaderboard || progress ? ' so-dash__panel--half' : ''}">` +
        `<div class="so-dash__panel-head">` +
        `<h3 class="so-dash__panel-title">${esc(data.chart.title)}</h3>` +
        `<div class="so-dash__legend">${legend}</div></div>` +
        `<div class="so-dash__bars">${bars}</div></div>` +
        leaderboard +
        progress +
        `</div></div>`;
    return shell(context, data.ticker, body, {
        variant: 'dark',
        className: 'so-tpl--dark',
        live: data.leaderboard?.live ?? false,
    });
}
/** 그림 자리의 장식 — 그림·영상을 고르지 않았거나 화면이 아직 받지 못했을 때. 공공 화면이라 안내 글은 쓰지 않는다. */
const PICTURE_MARK = '<svg class="so-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" ' +
    'stroke-linejoin="round" aria-hidden="true"><rect width="18" height="18" x="3" y="3" rx="2"/><circle cx="9" cy="9" r="2"/>' +
    '<path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>';
/**
 * 반반 화면의 HTML 칸이 남기는 자리 표시(2026-11-03) — 값은 올린 HTML 의 자산 id. 렌더러는 순수 함수라 iframe 을 만들지
 * 않는다 — 받는 쪽이 이 자리에 샌드박스로 띄운다(플레이어 `template-html.ts`, 콘솔 미리보기는 그 위에 겹친다).
 */
export const SPLIT_HTML_ATTR = 'data-so-html';
/** HTML 칸의 맞춤(`cover`·`contain`)과 보일 영역(`x,y,w,h` 비율) — 받는 쪽이 칸마다 읽는다(2026-11-03). */
export const HTML_SLOT_FIT_ATTR = 'data-so-html-fit';
export const HTML_SLOT_AREA_ATTR = 'data-so-html-area';
/** 올린 HTML 을 띄울 자리 — 장식 무늬 위에 덮는다(받는 쪽이 못 읽으면 걷어 무늬가 보인다). */
function htmlSlot(assetId, fit, area) {
    const areaAttr = area
        ? ` ${HTML_SLOT_AREA_ATTR}="${[area.x, area.y, area.w, area.h].map((value) => num(value, 0, 1).toFixed(4)).join(',')}"`
        : '';
    return (`<div class="so-split__placeholder">${PICTURE_MARK}</div>` +
        `<div class="so-split__html" ${SPLIT_HTML_ATTR}="${esc(assetId)}" ${HTML_SLOT_FIT_ATTR}="${fit}"${areaAttr}></div>`);
}
/** 칸의 보일 영역 글자(`x,y,w,h`)를 읽는다 — 모양이 틀리면 null(전체). */
export function parseHtmlSlotArea(value) {
    const parsed = htmlAreaSchema.safeParse(((parts) => (parts.length === 4 ? { x: parts[0], y: parts[1], w: parts[2], h: parts[3] } : null))((value ?? '').split(',').map(Number)));
    return parsed.success ? parsed.data : null;
}
/**
 * 반반 화면 — 그림 칸과 글 칸을 반씩. 나누는 방향(`so-split--auto|columns|rows`)과 크기는 CSS 가 캔버스 방향을 보고 정한다
 * (화면에 맞춰: 가로 화면은 좌우, 세로 화면은 위아래). 그림 자리(앞·뒤)는 칸의 차례로 정한다.
 *
 * 영상은 소리 없이 되풀이한다. `muted` 를 적어도 innerHTML 로 만든 영상은 소리가 날 수 있어 받는 쪽(플레이어)이 한 번 더
 * 끄고 튼다. HTML 칸은 장식 무늬 위에 자리(`SPLIT_HTML_ATTR`)만 둔다 — 받는 쪽이 그 HTML 을 못 읽으면 자리를 걷어 무늬가 보인다.
 */
function renderSplit(data, context) {
    const url = data.media ? context.media?.[data.media.assetId] : undefined;
    const fit = data.media?.fit === 'contain' ? 'contain' : 'cover';
    const placeholder = `<div class="so-split__placeholder">${PICTURE_MARK}</div>`;
    const picture = data.media && url
        ? data.media.kind === 'html'
            ? htmlSlot(data.media.assetId, fit, data.media.area)
            : data.media.kind === 'video'
                ? `<video class="so-split__video so-fit-${fit}" src="${esc(url)}" muted autoplay loop playsinline preload="auto"></video>`
                : `<img class="so-split__image so-fit-${fit}" src="${esc(url)}" alt="">`
        : placeholder;
    const look = data.media && url ? ` so-split__media--${fit}${data.media.kind === 'html' ? ' so-split__media--html' : ''}` : '';
    const media = `<section class="so-split__pane so-split__media${look}">` + picture + `</section>`;
    const pointTier = groupTier(data.points, [12, 22, 32]);
    const points = data.points.length
        ? `<div class="so-split__points">` +
            data.points
                .map((point) => `<div class="so-split__point">` +
                `<div class="so-tile-icon so-split__point-icon">${icon('check')}</div>` +
                `<span class="so-split__point-text so-clamp2 ${pointTier}">${esc(point)}</span></div>`)
                .join('') +
            `</div>`
        : '';
    const note = data.note
        ? `<div class="so-split__note">${icon('arrowRight')}<span class="so-one">${esc(data.note)}</span></div>`
        : '';
    const text = `<section class="so-split__pane so-card so-split__text">` +
        `<div class="so-split__copy">` +
        (data.badge
            ? `<div class="so-split__badge"><span class="so-badge">${icon('star')}<span class="so-one">${esc(data.badge)}</span></span></div>`
            : '') +
        `<h1 class="so-split__title so-clamp3 ${tierClass(data.title, [10, 20, 36])}">${esc(data.title)}</h1>` +
        (data.body
            ? `<p class="so-split__lead so-clamp4 ${tierClass(data.body, [50, 100, 180])}">${esc(data.body)}</p>`
            : '') +
        `</div>` +
        (points || note ? `<div class="so-split__aside">${points}${note}</div>` : '') +
        `</section>`;
    /*
     * 글 칸이 붐비면 한 단계 작게(`so-split--dense`) — 항목이 넷이거나, 셋 이상인데 제목·설명이 길 때. 글 칸 하나에 제목·설명·
     * 항목·한 줄이 모두 들어가야 해서, 길이 등급만으로는 긴 제목과 항목 넷이 함께 오면 칸을 넘긴다.
     */
    const dense = data.points.length >= 4 ||
        (data.points.length >= 3 && (sizeTier(data.title, [10, 20, 36]) >= 3 || Array.from(data.body).length > 60));
    const body = `<div class="so-body so-split so-split--${data.layout}${data.share !== '50' ? ` so-split--s${data.share}` : ''}${dense ? ' so-split--dense' : ''}">` +
        (data.mediaSide === 'end' ? text + media : media + text) +
        `</div>`;
    return shell(context, data.ticker, body);
}
/**
 * 구역 나누기에서 여러 장 가운데 지금 보일 장(2026-11-03) — 시각으로 정한다. 같은 내용을 트는 화면끼리 함께 넘어가고, 순수
 * 함수라 콘솔 미리보기(초마다 다시 그린다)도 같은 장을 보인다. 화면은 붙인 뒤 다음 경계마다 장을 바꾼다(`template-zones.ts`).
 */
export function zoneSlideIndex(nowMs, dwellSec, count) {
    if (count <= 1 || !(dwellSec > 0) || !Number.isFinite(nowMs))
        return 0;
    return Math.floor(nowMs / (dwellSec * 1000)) % count;
}
/** 구역의 장을 넘기는 간격(초) — 받는 쪽이 읽는다. 장이 둘 이상일 때만 붙는다. */
export const ZONE_ROTATE_ATTR = 'data-so-rotate';
/** 구역의 한 장 — 반반 화면의 그림 칸과 같은 조각(그림·소리 없이 되풀이하는 영상·HTML 칸)이다. */
function zonePicture(item, url) {
    const fit = item.fit === 'contain' ? 'contain' : 'cover';
    if (item.kind === 'html')
        return htmlSlot(item.assetId, fit, item.area);
    if (item.kind === 'video') {
        return `<video class="so-split__video so-fit-${fit}" src="${esc(url)}" muted autoplay loop playsinline preload="auto"></video>`;
    }
    return `<img class="so-split__image so-fit-${fit}" src="${esc(url)}" alt="">`;
}
function renderZone(zone, index, context) {
    const place = `so-zone so-zone--${index}`;
    if (zone?.kind === 'text') {
        return (`<section class="${place} so-zone--text so-card">` +
            (zone.badge
                ? `<div class="so-zone__badge"><span class="so-badge">${icon('star')}<span class="so-one">${esc(zone.badge)}</span></span></div>`
                : '') +
            `<h1 class="so-zone__title so-clamp3 ${tierClass(zone.title, [10, 20, 36])}">${esc(zone.title)}</h1>` +
            (zone.body ? `<p class="so-zone__lead so-clamp4 ${tierClass(zone.body, [50, 100, 180])}">${esc(zone.body)}</p>` : '') +
            `</section>`);
    }
    // 받는 쪽이 들고 있는 장만 — 아직 못 받은 장은 건너뛰고, 하나도 없으면 장식 무늬.
    const items = (zone?.items ?? []).flatMap((item) => {
        const url = context.media?.[item.assetId];
        return url ? [{ item, url }] : [];
    });
    if (items.length === 0) {
        return `<section class="${place} so-zone--media"><div class="so-split__placeholder">${PICTURE_MARK}</div></section>`;
    }
    const dwell = zone?.kind === 'media' ? zone.dwellSec : 10;
    const shown = zoneSlideIndex(context.now.getTime(), dwell, items.length);
    return (`<section class="${place} so-zone--media"${items.length > 1 ? ` ${ZONE_ROTATE_ATTR}="${num(dwell, 1, 3600)}"` : ''}>` +
        items
            .map(({ item, url }, at) => `<div class="so-zone__slide${at === shown ? ' so-zone__slide--on' : ''}${item.fit === 'contain' ? ' so-zone__slide--contain' : ''}">` +
            zonePicture(item, url) +
            `</div>`)
            .join('') +
        `</section>`);
}
/**
 * 구역 나누기(2026-11-03) — 본문을 모양대로 2~4 구역으로. 구역의 자리는 CSS 가 캔버스 방향을 보고 정한다(`ZONE_BOXES`).
 * 그림 구역은 반반 화면의 그림 칸과 같은 조각을 쓴다 — 영상은 소리 없이 되풀이하고, HTML 은 자리(`SPLIT_HTML_ATTR`)만 둔다.
 */
function renderZones(data, context) {
    const count = ZONE_COUNTS[data.layout];
    const body = `<div class="so-body so-zones so-zones--${data.layout}">` +
        Array.from({ length: count }, (_, index) => renderZone(data.zones[index], index, context)).join('') +
        `</div>`;
    return shell(context, data.ticker, body);
}
const DAY_MS = 86_400_000;
const dayIndex = (year, month, day) => Date.UTC(year, month - 1, day) / DAY_MS;
function isoDay(index) {
    const date = new Date(index * DAY_MS);
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}
/**
 * 목표 기간은 앞 배수를 채운 다음 날부터 이 배수를 채우는 날까지다(1~5 배수는 한 해씩, 5→7 은 두 해 …). 해를 더한 날이
 * 달력에 없으면(2월 29일 + 1년) 다음 날로 넘어간다 — 고객사 현황판 HTML 의 `new Date(y + n, m, d)` 와 같다.
 */
export function zeroAccidentFigures(startDate, multiplierValue, today) {
    const [year, month, day] = startDate.split('-').map(Number);
    const at = ZERO_ACCIDENT_MULTIPLIERS.indexOf(multiplierValue);
    const multiplier = at >= 0 ? Number(multiplierValue) : 1;
    const previous = at > 0 ? Number(ZERO_ACCIDENT_MULTIPLIERS[at - 1]) : 0;
    const base = dayIndex(year, month, day);
    const periodStart = dayIndex(year + previous, month, day);
    const end = dayIndex(year + multiplier, month, day) - 1;
    const now = dayIndex(today.year, today.month, today.day);
    const totalDays = end - periodStart + 1;
    const elapsedDays = Math.min(totalDays, Math.max(0, now - periodStart + 1));
    const remainingDays = totalDays - elapsedDays;
    const phase = now < periodStart ? 'before' : now > end ? 'done' : remainingDays === 0 ? 'today' : 'running';
    const dday = phase === 'before'
        ? `D-${periodStart - now}`
        : phase === 'done'
            ? `D+${now - end}`
            : phase === 'today'
                ? 'D-DAY'
                : `D-${remainingDays}`;
    return {
        multiplier,
        periodStart: isoDay(periodStart),
        end: isoDay(end),
        totalDays,
        elapsedDays,
        remainingDays,
        percent: Math.min(100, Math.max(0, (elapsedDays / totalDays) * 100)),
        cumulativeDays: Math.max(0, now - base + 1),
        startsIn: Math.max(0, periodStart - now),
        phase,
        dday,
    };
}
/** 세 자리마다 쉼표 — `toLocaleString` 은 받는 쪽의 ICU 에 따라 다를 수 있어 직접 쓴다. */
const commas = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
/** 무재해 현황의 날짜 표기 — `2026.03.01`. */
const dotted = (iso) => iso.replace(/-/g, '.');
/** D-day 위의 한 줄과 진행 막대 아래의 문장 — 단계마다. */
function zeroAccidentWords(figures) {
    const m = `${figures.multiplier}배수`;
    switch (figures.phase) {
        case 'before':
            return { label: `${m} 기간 시작까지`, status: `${m} 목표 기간 시작까지 ${commas(figures.startsIn)}일 남았습니다` };
        case 'today':
            return { label: `${m} 달성`, status: `오늘 ${m} 무재해 목표를 달성합니다` };
        case 'done':
            return { label: `${m} 달성`, status: `${m} 무재해 목표를 달성했습니다` };
        default:
            return { label: `${m} 달성까지`, status: `${m} 목표 달성까지 ${commas(figures.remainingDays)}일 남았습니다` };
    }
}
/** 무재해 배지의 방패 — 고를 수 있는 아이콘 목록(`ICON_NAMES`)에 넣지 않는다(이 템플릿만 쓴다). Lucide shield-check. */
const SHIELD_MARK = '<svg class="so-i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
    'stroke-linejoin="round" aria-hidden="true"><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1' +
    'c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/></svg>';
/**
 * 무재해 현황 — 위 큰 카드(배지·제목·부서·시작일 | D-day), 가운데 숫자 다섯 칸, 아래 목표 진행률 막대.
 *
 * 숫자는 렌더할 때 화면의 오늘로 센다 — 화면은 항목을 띄울 때마다 다시 그리므로 날이 바뀌면 따라 바뀐다. 숫자 칸에
 * `data-zero` 를 달아 [화면 편집] 사본(올린 HTML)의 스크립트가 같은 칸을 매일 고쳐 쓴다(`templateHtml.ts`).
 */
function renderZeroAccident(data, context) {
    const today = zonedDay(context.now, context.timezone);
    const figures = zeroAccidentFigures(data.startDate, data.multiplier, today);
    const words = zeroAccidentWords(figures);
    const percent = figures.percent.toFixed(1);
    const meta = [
        data.team ? `<span class="so-zero__meta-item">${icon('users')}<span class="so-one">${esc(data.team)}</span></span>` : '',
        `<span class="so-zero__meta-item">${icon('calendar')}<span class="so-one">무재해 시작일 ${esc(dotted(data.startDate))}</span></span>`,
        `<span class="so-zero__meta-item">${icon('activity')}<span class="so-one">누적 <b data-zero="cumulative">${commas(figures.cumulativeDays)}</b>일째</span></span>`,
    ].join('');
    const hero = `<section class="so-card so-zero__hero">` +
        `<div class="so-zero__intro">` +
        (data.badge
            ? `<div class="so-zero__badge"><span class="so-badge">${SHIELD_MARK}<span class="so-one">${esc(data.badge)}</span></span></div>`
            : '') +
        `<h1 class="so-zero__title so-one ${tierClass(data.title, [8, 12, 18])}">${esc(data.title)}</h1>` +
        `<div class="so-zero__meta">${meta}</div></div>` +
        `<div class="so-zero__dday so-zero__dday--${figures.phase}" data-zero-phase="${figures.phase}">` +
        `<div class="so-zero__dday-label so-one" data-zero="label">${esc(words.label)}</div>` +
        `<div class="so-zero__dday-value" data-zero="dday">${esc(figures.dday)}</div></div>` +
        `</section>`;
    const cells = [
        ['목표 배수', 'multiplier', String(figures.multiplier), '배수'],
        ['달성 예정일', 'end', dotted(figures.end), ''],
        ['전체 일수', 'total', commas(figures.totalDays), '일'],
        ['현재 달성 일수', 'elapsed', commas(figures.elapsedDays), '일'],
        ['달성률', 'percent', percent, '%'],
    ];
    const kpis = cells
        .map(([label, key, value, unit]) => `<div class="so-card so-zero__kpi${key === 'percent' ? ' so-zero__kpi--accent' : ''}">` +
        `<div class="so-zero__kpi-label so-one">${esc(label)}</div>` +
        `<div class="so-zero__kpi-value${key === 'end' ? ' so-zero__kpi-value--date' : ''}">` +
        `<span data-zero="${key}">${esc(value)}</span>` +
        (unit ? `<span class="so-zero__unit">${esc(unit)}</span>` : '') +
        `</div></div>`)
        .join('');
    const progress = `<section class="so-card so-zero__progress">` +
        `<div class="so-zero__progress-head">` +
        `<h3 class="so-zero__progress-title so-one">무재해 목표 진행률</h3>` +
        `<span class="so-zero__progress-pct"><span data-zero="percent">${percent}</span>%</span></div>` +
        `<div class="so-zero__track"><div class="so-zero__fill" data-zero="bar" style="width:${num(figures.percent)}%"></div></div>` +
        `<div class="so-zero__progress-foot">` +
        `<span class="so-zero__status so-one" data-zero="status">${esc(words.status)}</span>` +
        `<span class="so-zero__period" data-zero="period">${esc(dotted(figures.periodStart))} ~ ${esc(dotted(figures.end))}</span>` +
        `</div></section>`;
    const body = `<div class="so-body so-zero">${hero}<div class="so-zero__kpis">${kpis}</div>${progress}</div>`;
    return shell(context, data.ticker, body);
}
/**
 * 템플릿 본문을 렌더한다. 바깥 캔버스(`renderTemplateStage`)가 스케일을 담당한다.
 */
/**
 * 렌더 입구에서 페이로드를 한 번 정규화한다.
 *
 * **JSON 에는 Date 타입이 없다.** 스키마의 `deadline` 은 `z.coerce.date()` 라 파싱을
 * 거치면 `Date` 가 되지만, API 를 건너온 값은 언제나 문자열이다. 콘솔은 미리보기 전에
 * 스키마로 파싱해서 괜찮았고 플레이어는 받은 JSON 을 그대로 넘겨서, **같은 템플릿이
 * 콘솔에서는 멀쩡하고 화면에서만 `deadline.getTime is not a function` 으로 죽었다.**
 * 실패는 화면 안에만 남아 `last_error` 를 보기 전에는 아무도 모른다.
 *
 * 그래서 호출자에게 맡기지 않고 **여기서** 맞춘다. 이 파일이 "서버·콘솔·플레이어가 같은
 * 그림을 낸다"고 약속하는 자리인데, 넣어 주는 값을 호출자마다 다르게 전처리하면
 * 그 약속은 코드가 같다는 뜻밖에 되지 못한다.
 *
 * 파싱이 실패하면 무엇이 잘못됐는지 말하고 멈춘다 — 알 수 없는 TypeError 보다 낫다.
 * 이미 파싱된 값을 다시 파싱해도 결과는 같다(항목이 화면에 뜰 때 한 번 도는 경로다).
 */
function normalize(payload) {
    const parsed = templatePayloadSchema.safeParse(payload);
    if (parsed.success)
        return parsed.data;
    const first = parsed.error.issues[0];
    const where = first?.path.join('.') || '(최상위)';
    throw new Error(`템플릿 내용이 올바르지 않습니다 — ${where}: ${first?.message ?? '알 수 없는 오류'}`);
}
export function renderTemplate(input, context) {
    const payload = normalize(input);
    switch (payload.id) {
        case 'announcement':
            return renderAnnouncement(payload.data, context);
        case 'promotion':
            return renderPromotion(payload.data, context);
        case 'alert':
            return renderAlert(payload.data, context);
        case 'schedule':
            return renderSchedule(payload.data, context);
        case 'dashboard':
            return renderDashboard(payload.data, context);
        case 'split':
            return renderSplit(payload.data, context);
        case 'safety':
            return renderZeroAccident(payload.data, context);
        case 'zones':
            return renderZones(payload.data, context);
    }
}
/**
 * 컨테이너 크기에 맞춰 캔버스를 축소한 완성 HTML.
 *
 * **세로로 긴 컨테이너에는 세로 캔버스(1080×1920)를 쓴다.** 세로로 세운 화면에 가로
 * 캔버스를 그대로 넣으면 위아래가 화면의 2/3 넘게 비고 글자가 1/3 크기로 작아진다.
 * 그래도 비율이 조금 어긋나면 여백이 생긴다(contain). 잘라내는 것보다 낫다 —
 * 사이니지에서 잘린 글자는 곧 못 읽는 정보다.
 */
export function renderTemplateStage(payload, context, size) {
    const portrait = size.height > size.width;
    const width = portrait ? PORTRAIT_CANVAS_WIDTH : CANVAS_WIDTH;
    const height = portrait ? PORTRAIT_CANVAS_HEIGHT : CANVAS_HEIGHT;
    const scale = Math.min(size.width / width, size.height / height).toFixed(4);
    /*
     * 배율은 transform 으로 직접 적는다. CSS 변수(`--so-scale`)로 넘기던 때는 그 변수를 모르는
     * 안드로이드 5.1 웹뷰에서 배율이 통째로 버려졌다.
     */
    // 글꼴은 무대에 붙는 이름 하나로 고른다(`styles.ts` 의 `.so-font-*`). 고르지 않았으면 기본.
    const font = payload.font ?? DEFAULT_TEMPLATE_FONT;
    return (`<div class="so-tpl-stage so-font-${font}">` +
        `<div class="so-tpl-canvas${portrait ? ' so-tpl-canvas--portrait' : ''}" style="-webkit-transform:scale(${scale});transform:scale(${scale})">` +
        renderTemplate(payload, context) +
        `</div></div>`);
}
//# sourceMappingURL=render.js.map